import { useState, useEffect } from "react";
import { ragAPI } from "../utils/api";

const CATEGORIES = [
  { id: "all", label: "All Topics", icon: "📚" },
  { id: "structural", label: "IS Codes & Structure", icon: "🏛️" },
  { id: "cost", label: "Cost & Material Rates", icon: "💰" },
  { id: "planning", label: "Planning, Permits & Vastu", icon: "📐" },
  { id: "MEP", label: "Electrical & Plumbing (MEP)", icon: "⚡" },
  { id: "finishing", label: "Flooring, Doors & Paint", icon: "🎨" },
  { id: "green_building", label: "Solar & Green Energy", icon: "🌿" },
];

export default function KnowledgePage({ onAskAI }) {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      if (searchQuery.trim()) {
        const res = await ragAPI.search(searchQuery, activeCategory === "all" ? null : activeCategory);
        setDocs(
          res.results.map((r, i) => ({
            id: `search_${i}`,
            title: r.title,
            category: r.category,
            excerpt: r.excerpt,
            score: r.relevance_score,
            tags: [],
          }))
        );
      } else {
        const res = await ragAPI.getDocs(activeCategory === "all" ? null : activeCategory);
        setDocs(res.documents || []);
      }
    } catch (err) {
      console.error("Failed to fetch knowledge docs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, [activeCategory]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchDocs();
  };

  return (
    <div className="tool-page-container">
      <div className="page-header">
        <div className="header-badge">📚 IS-CODES &amp; KNOWLEDGE HUB</div>
        <h1>Indian Construction Knowledge Base 🏛️</h1>
        <p>Curated collection of BIS certified IS codes, municipal sanction processes, material consumption rules, and Vastu Shastra guidelines.</p>
      </div>

      {/* Search Input */}
      <form className="knowledge-search-form" onSubmit={handleSearchSubmit}>
        <div className="search-input-wrap">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-field"
            placeholder="Search IS 456, cement grades, black cotton soil, plumbing sizes, permit rules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="clear-search-btn"
              onClick={() => {
                setSearchQuery("");
                setTimeout(() => fetchDocs(), 10);
              }}
            >
              ✕
            </button>
          )}
          <button type="submit" className="search-submit-btn">
            Search
          </button>
        </div>
      </form>

      {/* Category Pills */}
      <div className="category-tabs-bar">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            className={`cat-pill ${activeCategory === cat.id ? "active" : ""}`}
            onClick={() => {
              setActiveCategory(cat.id);
              setSearchQuery("");
            }}
          >
            <span>{cat.icon}</span>
            <span>{cat.label}</span>
          </button>
        ))}
      </div>

      {/* Content Grid */}
      <div className="knowledge-cards-grid">
        {loading ? (
          <div className="loading-state-box">
            <div className="spinner" />
            <p>Loading construction knowledge database...</p>
          </div>
        ) : docs.length === 0 ? (
          <div className="empty-state-box">
            <div className="empty-icon">📂</div>
            <h3>No documents found</h3>
            <p>Try searching with different keywords or selecting a different category.</p>
          </div>
        ) : (
          docs.map((doc) => (
            <div key={doc.id} className="knowledge-card">
              <div className="k-card-header">
                <span className="k-cat-badge">{doc.category?.toUpperCase()}</span>
                {doc.score != null && (
                  <span className="k-score-badge" title="Cosine similarity between your search and this passage">
                    Similarity {doc.score.toFixed(2)}
                  </span>
                )}
              </div>
              <h3 className="k-card-title">{doc.title}</h3>
              <p className="k-card-excerpt">{doc.excerpt}</p>

              {doc.tags && doc.tags.length > 0 && (
                <div className="k-tags-list">
                  {doc.tags.slice(0, 4).map((tag, tIdx) => (
                    <span key={tIdx} className="k-tag">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="k-card-actions">
                <button
                  className="k-read-btn"
                  onClick={() =>
                    onAskAI(`Explain "${doc.title}" in detail, especially in the context of IS codes and Indian construction standards.`)
                  }
                >
                  💬 Ask AI About This Topic
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Bottom Educational Banner */}
      <div className="card standards-banner-card">
        <div className="banner-content">
          <div className="banner-icon">🏛️</div>
          <div>
            <h3>Key Indian Standards &amp; Codes Reference</h3>
            <p>
              • <strong>IS 456:2000</strong> — Plain &amp; Reinforced Concrete (RCC Design) <br />
              • <strong>IS 875 (Parts 1-5)</strong> — Structural Design Loads (Dead, Live, Wind) <br />
              • <strong>IS 1893:2016</strong> — Earthquake Resistant Structure Design <br />
              • <strong>IS 13920:2016</strong> — Ductile Detailing for Seismic Zones III, IV &amp; V <br />
              • <strong>NBC 2016</strong> — National Building Code of India
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
